import { Platform } from "react-native";

import Braze from "@/crossPlatform/Braze";
import { useSendbirdChat } from "@/crossPlatform/Sendbird";
import useAppMonitor from "@/services/AppMonitor";
import { isAndroid, isIOS } from "@/utils/platform.util";

interface UsePushTokenRegistration {
  registerPushToken: (token: string) => Promise<void>;
  unregisterPushToken: (token: string) => Promise<void>;
}

const usePushTokenRegistration = () => {
  const { sdk: sendbirdSdk } = useSendbirdChat();
  const { captureMessage } = useAppMonitor();

  const unregisterPushToken = async (token: string) => {
    if (!token) {
      return;
    }

    // Braze does not have a push token unregistration method
    if (isAndroid()) {
      await sendbirdSdk.unregisterFCMPushTokenForCurrentUser(token);
      return;
    }
    if (isIOS()) {
      await sendbirdSdk.unregisterAPNSPushTokenForCurrentUser(token);
      return;
    }
    captureMessage(
      `Unsupported platform for push token unregistration: ${Platform.OS}`,
    );
  };

  const registerPushToken = async (token: string) => {
    if (!token) {
      return;
    }

    try {
      Braze.registerPushToken(token);

      if (isAndroid()) {
        await sendbirdSdk.registerFCMPushTokenForCurrentUser(token);
      } else if (isIOS()) {
        await sendbirdSdk.registerAPNSPushTokenForCurrentUser(token);
      } else {
        captureMessage(
          `Unsupported platform for push token registration: ${Platform.OS}`,
        );
      }
    } catch {
      throw new Error("Push token registration failed");
    }
  };

  return {
    registerPushToken,
    unregisterPushToken,
  };
};

export type { UsePushTokenRegistration };
export default usePushTokenRegistration;
